import React, { useState, useEffect, useRef } from "react";
import { auth } from "./utils/firebase";
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { useMfaSignIn } from './utils/useMfaSignIn';
import { requestPasswordReset } from './utils/passwordResetService';
import { requestEmailVerification } from './utils/verificationService';
import { useNavigate, Link } from "react-router-dom";
import logo from "./assets/featherglass-sq-logo.png";
import { companyName, versionNum } from "./globals";
import { getFirebaseErrorMessage } from "./utils/errorHandler";
import { wasSessionExpired, clearSessionExpiredFlag } from "./utils/sessionTimeout";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { CircleCheck, CircleX, Eye, EyeClosed } from "lucide-react";
import GoogleIcon from "@mui/icons-material/Google";
import { firstLogin } from "./utils/loginUtils";
import { getData } from "./utils/firestoreService";
import { logAuditEventAsync, AuditActions } from "./utils/auditLogger";

const Login = () => {
  const { signInWithMfa, mfaPrompt } = useMfaSignIn();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [messageType, setMessageType] = useState("error");
  const navigate = useNavigate();

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isPasswordResetLoading, setIsPasswordResetLoading] = useState(false);
  const [isVerificationSending, setIsVerificationSending] = useState(false);
  const [verificationUser, setVerificationUser] = useState(null);
  const [verificationSent, setVerificationSent] = useState(false);

  // Validation states
  const [emailValidation, setEmailValidation] = useState({ isValid: null });
  const [passwordValidation, setPasswordValidation] = useState({ isValid: null });

  // UI states
  const [showPassword, setShowPassword] = useState(false);

  // Refs for auto-focus
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  useEffect(() => {
    // Check if user is already signed in
    const checkAuthState = () => {
      if (auth.currentUser) {
        navigate("/dashboard");
      }
    };
    checkAuthState();

    // Check if user was signed out due to session timeout
    if (wasSessionExpired()) {
      setError(getFirebaseErrorMessage({ code: 'auth/session-expired' }, "login"));
      clearSessionExpiredFlag();
    }

    // Check if user just verified their email
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("verified") === "true") {
      setSuccess("Email verified successfully! Please sign in to continue.");
    }

    if (urlParams.get("reset") === "success") {
      setSuccess("Password reset successfully! Please sign in with your new password.");
    }
  }, [navigate]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Real-time validation handlers
  const handleEmailChange = (e) => {
    setVerificationUser(null);
    setVerificationSent(false);
    const value = e.target.value;
    setEmail(value);

    if (!value) {
      setEmailValidation({ isValid: null });
      return;
    }

    const isValid = validateEmail(value);

    setEmailValidation({ isValid });
  };

  const handlePasswordChange = (e) => {
    setVerificationUser(null);
    setVerificationSent(false);
    const value = e.target.value;
    setPassword(value);

    if (!value) {
      setPasswordValidation({ isValid: null });
      return;
    }

    const isValid = value.trim().length > 0;

    setPasswordValidation({ isValid });
  };

  // Handle Enter key navigation
  const handleKeyPress = (e, nextRef, action) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (action) {
        action();
      } else if (nextRef && nextRef.current) {
        nextRef.current.focus();
      }
    }
  };

  const handleLogin = async () => {
    if (isLoading || isGoogleLoading || isVerificationSending) return;
    setVerificationUser(null);
    setVerificationSent(false);
    // Validate fields before proceeding
    if (!emailValidation.isValid) {
      setError(getFirebaseErrorMessage({ code: 'validation/invalid-email' }, "login"));
      emailRef.current?.focus();
      return;
    }

    if (!passwordValidation.isValid) {
      setError(getFirebaseErrorMessage({ code: 'validation/missing-password' }, "login"));
      passwordRef.current?.focus();
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const userCredential = await signInWithMfa(() => signInWithEmailAndPassword(auth, email, password));
      const user = userCredential.user;

      // Firebase Auth is authoritative. The only Firestore override accepted is
      // an explicitly attributed admin verification, not a bare client-writable
      // emailVerified boolean.
      let isManuallyVerified = false;
      try {
        const userData = await getData(`users/${user.uid}`);
        if (!userData) {
          await auth.signOut();
          setError('Your signup is not complete. Return to Sign up with your invitation or code and use the same email and password.');
          return;
        }
        isManuallyVerified = userData?.emailVerified === true
          && !!userData?.manuallyVerifiedBy
          && !!userData?.manuallyVerifiedAt;
      } catch (firestoreError) {
        console.error("Error checking Firestore verification status:", firestoreError);
        // Continue with Firebase Auth check only
      }

      if (user.emailVerified || isManuallyVerified) {
        await firstLogin(user, "email", setSuccess, navigate, setError);
      } else {
        // Sign out the user immediately
        await auth.signOut();
        setVerificationUser(user);
        setError(getFirebaseErrorMessage({ code: 'auth/email-not-verified' }, "login"));
        return;
      }
    } catch (error) {
      // Audit log: login failure
      logAuditEventAsync(AuditActions.AUTH_LOGIN_FAILURE, {
        userEmail: email,
        metadata: { errorCode: error.code, loginType: 'email' },
      });
      // Use centralized error handling
      const friendlyMessage = getFirebaseErrorMessage(error, "login");
      setError(friendlyMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setVerificationUser(null);
    setVerificationSent(false);
    setIsGoogleLoading(true);
    setError("");

    const provider = new GoogleAuthProvider();

    try {
      const result = await signInWithMfa(() => signInWithPopup(auth, provider));
      const user = result.user;

      // Check if user document exists in Firestore
      const userDoc = await getData(`users/${user.uid}`);

      if (!userDoc) {
        await auth.signOut();
        setError(
          <span>
            No account found. Please{" "}
            <Link to="/signup" style={{ color: "var(--color-link)", textDecoration: "underline" }}>
              sign up with an invite code
            </Link>
          </span>
        );
        return;
      }

      await firstLogin(user, "google", setSuccess, navigate, setError);
    } catch (error) {
      // Audit log: login failure
      logAuditEventAsync(AuditActions.AUTH_LOGIN_FAILURE, {
        userEmail: email,
        metadata: { errorCode: error.code, loginType: 'google' },
      });
      const friendlyMessage = getFirebaseErrorMessage(error, "login");
      setError(friendlyMessage);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleVerificationResend = async () => {
    if (!verificationUser || isVerificationSending || verificationSent) return;
    setIsVerificationSending(true);
    const result = await requestEmailVerification(verificationUser);
    if (result.success) {
      setVerificationSent(true);
      setSuccess("A verification email has been sent. Check your inbox and spam folder, then verify your email before signing in.");
    } else {
      setError(result.error || "Verification email could not be delivered. Please try again.");
    }
    setIsVerificationSending(false);
  };

  const handlePasswordRecovery = async () => {
    if (!emailValidation.isValid) {
      setError(getFirebaseErrorMessage({ code: 'validation/invalid-email' }, "passwordReset"));
      emailRef.current?.focus();
      return;
    }

    setIsPasswordResetLoading(true);
    setError("");

    try {
      setSuccess(await requestPasswordReset(email));
    } catch (error) {
      const friendlyMessage = getFirebaseErrorMessage(error, "passwordReset");
      setError(friendlyMessage);
    } finally {
      setIsPasswordResetLoading(false);
    }
  };

  // Helper function to set error messages
  const setError = (message) => {
    setErrorMessage(message);
    setMessageType("error");
  };

  // Helper function to set success messages
  const setSuccess = (message) => {
    setErrorMessage(message);
    setMessageType("success");
  };

  return (
    <div className="login-container">
      <img src={logo} alt="Logo" className="logo-login" />
      <h2 className="title-login">Welcome Back</h2>
      <p className="subtitle">Please sign in to your account</p>

      {!mfaPrompt && (
        <>
          <div className="input-container">
            <input
              ref={emailRef}
              type="email"
              value={email}
              onChange={handleEmailChange}
              onKeyDown={(e) => handleKeyPress(e, passwordRef)}
              placeholder="Email"
              className={`main-input ${emailValidation.isValid === false ? "input-error" : emailValidation.isValid === true ? "input-success" : ""}`}
              disabled={isLoading || isGoogleLoading || isVerificationSending}
            />
            <div className="input-validation">
              {emailValidation.isValid === true && <CircleCheck className="validation-icon success" />}
              {emailValidation.isValid === false && <CircleX className="validation-icon error" />}
            </div>
          </div>

          <div className="input-container">
            <input
              ref={passwordRef}
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={handlePasswordChange}
              onKeyDown={(e) => handleKeyPress(e, null, handleLogin)}
              placeholder="Password"
              className={`main-input ${passwordValidation.isValid === false ? "input-error" : passwordValidation.isValid === true ? "input-success" : ""}`}
              disabled={isLoading || isGoogleLoading || isVerificationSending}
            />
            <div className="input-validation">
              <Tooltip title={showPassword ? "Hide password" : "Show password"} arrow>
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="password-toggle">
                  {showPassword ? <EyeClosed /> : <Eye />}
                </button>
              </Tooltip>
              {passwordValidation.isValid === true && <CircleCheck className="validation-icon success" />}
              {passwordValidation.isValid === false && <CircleX className="validation-icon error" />}
            </div>
          </div>

          {errorMessage && <p className={`${messageType === "error" ? "login-error-message" : "login-success-message"}`}>{errorMessage}</p>}

          {verificationUser && (
            <button onClick={handleVerificationResend} className="center-button secondary"
              disabled={isLoading || isGoogleLoading || isVerificationSending || verificationSent}>
              {isVerificationSending ? "Sending verification email…" : verificationSent ? "Verification email sent" : "Resend verification email"}
            </button>
          )}

          <button onClick={handleLogin} className="center-button primary" disabled={isLoading || isGoogleLoading || isVerificationSending || !emailValidation.isValid || !passwordValidation.isValid}>
            {isLoading ? <CircularProgress size={20} color="inherit" /> : "Sign In"}
          </button>

          <div className="divider">
            <span>Or continue with</span>
          </div>

          <button onClick={handleGoogleLogin} className="center-button secondary" disabled={isLoading || isGoogleLoading || isVerificationSending}>
            {isGoogleLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <>
                <GoogleIcon style={{ fontSize: "var(--text-sm)", marginRight: "12px", marginTop: "2px" }} />
                Sign in with Google
              </>
            )}
          </button>

          <div className="auth-links">
            <button onClick={handlePasswordRecovery} className="link-button" disabled={isPasswordResetLoading || isVerificationSending || !emailValidation.isValid}>
              {isPasswordResetLoading ? <CircularProgress size={16} /> : "Forgot Password?"}
            </button>

            <Link to="/signup" className="link-button">
              Don't have an account? Sign up
            </Link>
          </div>
        </>
      )}
      {mfaPrompt}

      <footer className="footer">
        <p>
          &copy; {new Date().getFullYear()} {companyName} v{versionNum}
          <br />
          All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default Login;
